import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react";
import type { CSSProperties, RefCallback } from "react";

export type SelectFloatingPlacement = "top-left" | "bottom-left" | "top-right" | "bottom-right";

const FLOATING_PLACEMENTS = {
  "top-left": "top-start",
  "bottom-left": "bottom-start",
  "top-right": "top-end",
  "bottom-right": "bottom-end",
} as const;

/** Maps the select family's preferred placement to Floating UI. */
export function getSelectFloatingPlacement(
  placement: SelectFloatingPlacement = "bottom-left",
): (typeof FLOATING_PLACEMENTS)[SelectFloatingPlacement] {
  return FLOATING_PLACEMENTS[placement];
}

interface SelectFloatingOptions {
  open: boolean;
  placement?: SelectFloatingPlacement;
  usePortal?: boolean;
  menuSize?: "select" | "searchable-select" | "checkbox-multi-select";
  maxHeight?: number;
}

interface SelectFloatingReturn {
  referenceRef: RefCallback<HTMLElement>;
  menuRef: RefCallback<HTMLElement>;
  opensAbove: boolean;
  menuProps: { style: CSSProperties };
}

/** Shares collision handling and available-space limits across anchored and portalled selects. */
export function useSelectFloating({
  open,
  placement,
  usePortal = false,
  menuSize = "select",
  maxHeight,
}: SelectFloatingOptions): SelectFloatingReturn {
  const {
    refs,
    floatingStyles,
    placement: resolvedPlacement,
  } = useFloating<HTMLElement>({
    open,
    placement: getSelectFloatingPlacement(placement),
    strategy: usePortal ? "fixed" : "absolute",
    middleware: [
      offset(-1),
      flip({ padding: 5 }),
      shift({ padding: 5 }),
      size({
        padding: 5,
        apply({ availableHeight, availableWidth, rects, elements }) {
          elements.floating.style.setProperty("--select-available-height", `${Math.max(0, availableHeight)}px`);
          elements.floating.style.setProperty("--select-available-width", `${Math.max(0, availableWidth)}px`);
          elements.floating.style.minWidth = `${Math.min(rects.reference.width, Math.max(0, availableWidth))}px`;
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  return {
    referenceRef: refs.setReference,
    menuRef: refs.setFloating,
    opensAbove: resolvedPlacement.startsWith("top"),
    menuProps: {
      style: {
        ...floatingStyles,
        pointerEvents: usePortal ? "auto" : undefined,
        maxHeight: `min(${maxHeight == null ? `var(--${menuSize}-menu-max-height)` : `${maxHeight}px`}, var(--select-available-height, 100vh))`,
        maxWidth: "var(--select-available-width, 100vw)",
      },
    },
  };
}
